import ExportTypeCombo from '../common/ExportTypeCombo';
import {
  useStaffReportQuery,
  useStaffReportExportQuery
} from '@/services/staff.service';
import { useEffect, useMemo, useRef, useState } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row as ColumnRow } from 'primereact/row';
import { RefreshCw } from 'lucide-react';
import {
  formatNumber,
  formatText
} from '@/components/ReportMangers/common/utils/reportUtils';
import NoDataFound from '@/components/Common/NoDataFound';
import TableSkeletonShell from '@/components/Skeletons/TableSkeletonShell';
import { ROUTE_PATHS } from '@/constants';
import DatePicker from '@/components/UI/DatePicker';

interface StaffReportItem {
  staff_id: number | null;
  staff_name: string;
  monthly_pay_rate: string | number;
  federal_tax: string | number;
  after_tax: string | number;
  health_insurance: string | number;
  pension: string | number;
  insurance: string | number;
  net: string | number;
}

interface StaffReportTotals {
  total_gross: number;
  total_tax: number;
  total_after_tax: number;
  total_health_insurance: number;
  total_pension: number;
  total_insurance: number;
  total_net: number;
}

type StaffRow = StaffReportItem & { __selected?: boolean };

const StaffReport = () => {
  // Helper to convert a local date to a UTC-aligned Date object
  // (so that toISOString().slice(0,10) matches the local date)
  const toUTCStart = (date: Date) => {
    const d = new Date(date);
    d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
    return d;
  };

  // Helper to get first day of month (UTC aligned)
  const getFirstDayOfMonth = (date: Date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), 1);
    return toUTCStart(d);
  };

  // Helper to get last day of month (UTC aligned)
  const getLastDayOfMonth = (date: Date) => {
    const d = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    return toUTCStart(d);
  };

  const getDefaultDates = () => {
    const today = new Date();
    // Start of previous month
    const startMonth = new Date(today);
    startMonth.setMonth(startMonth.getMonth() - 1);
    const start = getFirstDayOfMonth(startMonth);

    // End of current month
    // Note: getLastDayOfMonth is usually based on 'today's month
    const end = getLastDayOfMonth(today);

    return { start, end };
  };

  const defaults = getDefaultDates();
  const [fromDate, setFromDate] = useState<Date | null>(defaults.start);
  const [toDate, setToDate] = useState<Date | null>(defaults.end);
  const [payload, setPayload] = useState<string>('');
  const [exportType, setExportType] = useState<string>('pdf');
  const [selectedInstructorIds, setSelectedInstructorIds] = useState<
    Set<number>
  >(new Set());
  const [exportPayload, setExportPayload] = useState<string>('');
  const [exportEnabled, setExportEnabled] = useState<boolean>(false);

  // Format date to YYYY-MM-DD
  const formatDatePayload = (date: Date | null) => {
    if (!date) return '';
    return date.toISOString().split('T')[0];
  };

  useEffect(() => {
    // keep payload in sync with selected dates
    const params: Record<string, string> = {};
    const start = formatDatePayload(fromDate);
    const end = formatDatePayload(toDate);

    if (start) params.start = start;
    if (end) params.end = end;

    const qs = new URLSearchParams(params).toString();
    setPayload(qs);
  }, [fromDate, toDate]);

  const initialQs = new URLSearchParams({
    start: formatDatePayload(defaults.start),
    end: formatDatePayload(defaults.end)
  }).toString();

  const {
    data: staffReport,
    isLoading: staffReportLoading,
    refetch,
    isFetching
  } = useStaffReportQuery(payload || initialQs);

  const apiData =
    (staffReport as unknown as {
      items?: StaffReportItem[];
      total?: StaffReportTotals;
    }) || {};

  const reportItems: StaffReportItem[] = useMemo(() => {
    const items = apiData.items ?? [];
    // Filter out any row where staff_name is "Total" or staff_id is null
    return items.filter(
      item =>
        item.staff_id !== null &&
        item.staff_name &&
        item.staff_name.toLowerCase() !== 'total'
    );
  }, [apiData.items]);

  const reportTotals: StaffReportTotals = useMemo(() => {
    const items = apiData.items ?? [];
    // Find the Total row in items array
    const totalRow = items.find(
      item =>
        item.staff_id === null ||
        (item.staff_name && item.staff_name.toLowerCase() === 'total')
    );

    if (totalRow) {
      return {
        total_gross: Number(totalRow.monthly_pay_rate) || 0,
        total_tax: Number(totalRow.federal_tax) || 0,
        total_after_tax: Number(totalRow.after_tax) || 0,
        total_health_insurance: Number(totalRow.health_insurance) || 0,
        total_pension: Number(totalRow.pension) || 0,
        total_insurance: Number(totalRow.insurance) || 0,
        total_net: Number(totalRow.net) || 0
      };
    }

    return {
      total_gross: 0,
      total_tax: 0,
      total_after_tax: 0,
      total_health_insurance: 0,
      total_pension: 0,
      total_insurance: 0,
      total_net: 0
    };
  }, [apiData.items]);

  const { isFetching: isExporting } = useStaffReportExportQuery(
    exportPayload,
    exportEnabled
  );

  const isTableLoading = staffReportLoading || isFetching;

  // Select all checkbox indeterminate handling
  const selectAllRef = useRef<HTMLInputElement | null>(null);
  const allIds = useMemo(
    () => reportItems.map(r => Number(r.staff_id)).filter(Boolean),
    [reportItems]
  );
  const allSelected =
    selectedInstructorIds.size === allIds.length && allIds.length > 0;
  const someSelected = selectedInstructorIds.size > 0 && !allSelected;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const handleToggleInstructor = (id: number) => {
    setSelectedInstructorIds(prev => {
      const next = new Set<number>(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleToggleAll = () => {
    setSelectedInstructorIds(_prev => {
      if (allSelected) {
        return new Set<number>();
      }
      return new Set<number>(allIds);
    });
  };

  const tableValue = useMemo<StaffRow[]>(
    () =>
      reportItems.map(item => ({
        ...item,
        __selected: selectedInstructorIds.has(Number(item.staff_id))
      })),
    [reportItems, selectedInstructorIds]
  );

  const headerClassName =
    'text-2xs md:text-xs font-semibold uppercase tracking-wide text-slate-600 bg-slate-50';
  const bodyClassName = 'text-xs md:text-sm text-slate-700';

  const renderSelectHeader = () => (
    <div className='flex items-center justify-center h-full'>
      <input
        type='checkbox'
        ref={ref => {
          if (ref) ref.indeterminate = someSelected;
          selectAllRef.current = ref;
        }}
        checked={allSelected}
        onChange={handleToggleAll}
        className='w-3 h-3 md:w-4 md:h-4'
        aria-label='Select all instructors'
      />
    </div>
  );

  const renderSelectBody = (row: StaffRow) => {
    const id = Number(row.staff_id);
    const isSelected = selectedInstructorIds.has(id);

    return (
      <input
        type='checkbox'
        checked={isSelected}
        onChange={() => handleToggleInstructor(id)}
        className='w-3 h-3 md:w-4 md:h-4'
        aria-label={`Select staff ${row.staff_name || id}`}
      />
    );
  };

  const footerGroup = useMemo(
    () => (
      <ColumnGroup>
        <ColumnRow>
          {/* Checkbox column footer empty */}
          <Column
            footer=''
            footerClassName={bodyClassName}
            style={{ width: '3rem', minWidth: '3rem' }}
          />
          {/* Label under Staff Name */}
          <Column
            footer='Total:'
            footerClassName={`${bodyClassName} text-left font-semibold`}
          />
          <Column
            footer={formatNumber(reportTotals.total_gross)}
            footerClassName={bodyClassName}
          />
          <Column
            footer={formatNumber(reportTotals.total_tax)}
            footerClassName={`${bodyClassName} text-red-600`}
          />
          <Column
            footer={formatNumber(reportTotals.total_after_tax)}
            footerClassName={bodyClassName}
          />
          <Column
            footer={formatNumber(reportTotals.total_health_insurance)}
            footerClassName={`${bodyClassName} text-orange-600`}
          />
          <Column
            footer={formatNumber(reportTotals.total_pension)}
            footerClassName={`${bodyClassName} text-orange-600`}
          />
          <Column
            footer={formatNumber(reportTotals.total_insurance)}
            footerClassName={`${bodyClassName} text-orange-600`}
          />
          <Column
            footer={formatNumber(reportTotals.total_net)}
            footerClassName={`${bodyClassName} text-green-600 font-semibold`}
          />
        </ColumnRow>
      </ColumnGroup>
    ),
    [bodyClassName, reportTotals]
  );

  const handleExport = () => {
    const params: Record<string, string> = {};

    // Date range
    const start = formatDatePayload(fromDate);
    const end = formatDatePayload(toDate);
    if (start) params.start = start;
    if (end) params.end = end;

    // Pagination (disabled for export)
    params.pagination = 'false';

    // Staff IDs - only if specific staff are selected
    if (!allSelected && selectedInstructorIds.size > 0) {
      // Send individual staff_id for each selected staff
      const selectedIds = Array.from(selectedInstructorIds);
      if (selectedIds.length === 1) {
        params.staff_id = selectedIds[0].toString();
      } else {
        // For multiple selections, send comma-separated
        params.staff_ids = selectedIds.join(',');
      }
    }

    // Export type
    params.type = exportType || 'pdf';
    let qs = new URLSearchParams(params).toString();
    qs = `${qs}&_ts=${Date.now()}`;

    setExportPayload(qs);
    setExportEnabled(true);
  };
  return (
    <div className='relative h-[calc(100vh-50px)] flex flex-col overflow-hidden'>
      <div className='p-4 bg-gradient-to-br from-slate-50 to-white flex-1 flex flex-col overflow-hidden'>
        <div className='bg-white rounded-2xl shadow-sm border border-gray-200 mt-4 flex-1 flex flex-col overflow-hidden'>
          {/* Top controls (Period Selector + Export Filter) */}
          <div className='flex-none flex flex-col md:flex-row flex-wrap items-start md:items-center gap-4 px-6 pt-6 pb-2'>
            <span className='text-sm text-slate-700 font-medium whitespace-nowrap'>
              Date Range:
            </span>
            <div className='flex flex-col sm:flex-row gap-3 items-center w-full md:w-auto'>
              <DatePicker
                value={fromDate}
                onChange={d => {
                  if (!d) {
                    setFromDate(null);
                    return;
                  }
                  const nextStart = getFirstDayOfMonth(d);
                  setFromDate(nextStart);

                  if (toDate && nextStart > toDate) {
                    setToDate(getLastDayOfMonth(nextStart));
                  }
                }}
                filterDate={(date: Date) => date.getDate() === 1}
                className='w-full sm:w-44 border border-gray-300 rounded-lg px-2 py-1.5 bg-white'
              />
              <span className='text-gray-500 font-medium hidden sm:inline'>
                –
              </span>
              <DatePicker
                value={toDate}
                onChange={d => {
                  if (!d) {
                    setToDate(null);
                    return;
                  }
                  const nextEnd = getLastDayOfMonth(d);
                  setToDate(nextEnd);

                  if (fromDate && nextEnd < fromDate) {
                    setFromDate(getFirstDayOfMonth(nextEnd));
                  }
                }}
                filterDate={(date: Date) => {
                  const d = new Date(date);
                  // Check if this date is the last day of its month
                  return (
                    new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate() ===
                    d.getDate()
                  );
                }}
                className='w-full sm:w-44 border border-gray-300 rounded-lg px-2 py-1.5 bg-white'
              />
            </div>
            <button
              onClick={() => refetch()}
              disabled={isFetching}
              className='flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors text-sm w-full md:w-auto'
              aria-label='Get Report'
            >
              <RefreshCw
                className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`}
              />
              Get Report
            </button>
            <div className='flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full md:w-auto'>
              <span className='text-sm text-slate-700 font-medium whitespace-nowrap'>
                Export Filter:
              </span>
              <div className='flex flex-row gap-3 items-center w-full sm:w-auto'>
                <ExportTypeCombo
                  reportKey='STAFF_REPORT'
                  value={exportType}
                  onChange={v => setExportType(v)}
                  className='w-full sm:w-40 max-w-xs min-w-[120px]'
                />
                <button
                  onClick={handleExport}
                  disabled={isExporting}
                  className='flex items-center justify-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg font-semibold hover:bg-blue-600 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors text-sm flex-1 sm:flex-none'
                  aria-label='Export'
                >
                  <RefreshCw
                    className={`w-4 h-4 ${isExporting ? 'animate-spin' : ''}`}
                  />
                  Export
                </button>
              </div>
            </div>
          </div>
          <div className='p-2 flex-1 overflow-hidden'>
            <div className='rounded-2xl border border-gray-200 overflow-hidden h-full flex flex-col'>
              {isTableLoading ? (
                <TableSkeletonShell rows={10} columns={9} />
              ) : (
                <DataTable
                  value={tableValue}
                  scrollable
                  scrollHeight='flex'
                  sortMode='multiple'
                  loading={false}
                  emptyMessage={
                    <NoDataFound
                      text='No data available in table'
                      description='Try adjusting your search criteria or add a new staff record'
                    />
                  }
                  tableStyle={{ minWidth: '70rem', tableLayout: 'fixed' }}
                  className='text-xs md:text-sm'
                  footerColumnGroup={footerGroup}
                  rowClassName={(row: StaffRow) =>
                    row.__selected ? 'bg-blue-50' : ''
                  }
                >
                  <Column
                    header={renderSelectHeader}
                    body={renderSelectBody}
                    style={{ width: '3rem', minWidth: '3rem' }}
                    headerClassName={`${headerClassName} text-center`}
                    bodyClassName={`${bodyClassName} text-center`}
                  />
                  <Column
                    field='staff_name'
                    header='Staff Name'
                    body={(row: StaffReportItem) => (
                      <button
                        className='text-blue-600 hover:underline'
                        onClick={() => {
                          const url = `${ROUTE_PATHS.STAFF_MANAGER_REPORT}/${row.staff_id}?start=${formatDatePayload(
                            fromDate
                          )}&end=${formatDatePayload(toDate)}`;
                          window.open(url, '_blank');
                        }}
                      >
                        {formatText(row.staff_name)}
                      </button>
                    )}
                    sortable
                    headerClassName={headerClassName}
                    bodyClassName={bodyClassName}
                  />
                  <Column
                    field='monthly_pay_rate'
                    header='Gross'
                    body={(row: StaffReportItem) =>
                      formatNumber(row.monthly_pay_rate)
                    }
                    sortable
                    headerClassName={headerClassName}
                    bodyClassName={bodyClassName}
                  />
                  <Column
                    field='federal_tax'
                    header='Tax'
                    body={(row: StaffReportItem) =>
                      formatNumber(row.federal_tax)
                    }
                    sortable
                    headerClassName={headerClassName}
                    bodyClassName={`${bodyClassName} text-red-600`}
                  />
                  <Column
                    field='after_tax'
                    header='After Tax'
                    body={(row: StaffReportItem) => formatNumber(row.after_tax)}
                    sortable
                    headerClassName={headerClassName}
                    bodyClassName={bodyClassName}
                  />
                  <Column
                    field='health_insurance'
                    header='Health Insurance'
                    body={(row: StaffReportItem) =>
                      formatNumber(row.health_insurance)
                    }
                    sortable
                    headerClassName={headerClassName}
                    bodyClassName={`${bodyClassName} text-orange-600`}
                  />
                  <Column
                    field='pension'
                    header='Pension'
                    body={(row: StaffReportItem) => formatNumber(row.pension)}
                    sortable
                    headerClassName={headerClassName}
                    bodyClassName={`${bodyClassName} text-orange-600`}
                  />
                  <Column
                    field='insurance'
                    header='Insurance'
                    body={(row: StaffReportItem) => formatNumber(row.insurance)}
                    sortable
                    headerClassName={headerClassName}
                    bodyClassName={`${bodyClassName} text-orange-600`}
                  />
                  <Column
                    field='net'
                    header='Net'
                    body={(row: StaffReportItem) => formatNumber(row.net)}
                    sortable
                    headerClassName={headerClassName}
                    bodyClassName={`${bodyClassName} text-green-600 font-semibold`}
                  />
                </DataTable>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffReport;
